/**
 * toggleActions module
 * Provides utility functions for toggling, filtering, and sorting grid/table rows in the UI.
 * Includes Alpine.js component initialization for interactive grid tables.
 *
 * Functions:
 * - init(): Initializes Alpine.js gridTable component.
 * - hideTakenPlayers(element):
 *     Used on stats result pages to show/hide players already on a roster.
 *     Adds/removes the -noStripeCount class to rows with .m-playerStatResult.
 *     @param {object} element - Toggle DOM element (checkbox)
 * - toggleRows(toggleElement, containerId, attributeName, valuesToFilter):
 *     Toggles visibility of rows within a container based on a data attribute and values.
 *     Adds/removes the -noStripeCount class to rows that do not/do match the filter.
 *     @param {object} toggleElement - Toggle DOM element (checkbox)
 *     @param {string} containerId - ID of the container element (optional)
 *     @param {string} attributeName - Data attribute name (without 'data-')
 *     @param {Array|string|number} valuesToFilter - Values to match against the attribute
 * - filterRows(filters, containerId):
 *     Filters rows within a container based on multiple attribute/value pairs.
 *     Adds/removes the -noStripeCount class to rows that do not/do match the filters.
 *     @param {Array} filters - Array of filter objects: [{attr: 'team', values: ['NE', 'DET']}]
 *     @param {string} containerId - ID of the container element (optional)
 * - runCombinedFilter(formId, containerId):
 *     Attaches event listeners and runs filterRows for any form fields.
 *     Each input/select in the form should have a data-filter-attr attribute.
 *     @param {string} formId - ID of the form containing filter fields
 *     @param {string} containerId - ID of the container to filter
 * - alpineCode():
 *     Initializes Alpine.js gridTable component for interactive sorting.
 *     The gridTable component tracks sort order for each attribute and provides sortGridTable(attribute) for sorting rows.
 *
 * Alpine gridTable component:
 * - sortOrders: Object tracking current sort order for each attribute.
 * - sortGridTable(attribute): Sorts grid rows by the given data attribute and toggles sort order for subsequent clicks.
 */

const toggleActions = (function () {
    function init() {
        alpineCode();
    }
    /**
     * Used on stats result page to show/hide players that are already on a roster.
     * Use nth-child(even of :not(-noStripeCount)) in CSS to apply colored backgound
     * since -noStripeCount is applied to rows with players that are taken. Taken player
     * rows are assigned the class .m-playerStatResult to indicate they are taken.
     *
     * @param {object} element toggle DOM element
     */
    function hideTakenPlayers(element) {
        const takenPlayers = document.querySelectorAll(".m-playerStatResult");
        for (let i = 0; i < takenPlayers.length; i++) {
            const playerRow = takenPlayers[i];
            if (element.checked) {
                playerRow.classList.add("-noStripeCount");
            } else {
                playerRow.classList.remove("-noStripeCount");
            }
        }
    }

    /**
     * Initializes Alpine.js data components for grid table sorting and filtering.
     *
     * Registers two Alpine data components:
     *  - "gridTable": Provides sorting functionality for grid table rows based on data attributes.
     *  - "gridTableFilter": Enables filtering of grid table rows using form fields with data-filter-attr attributes.
     *
     * The sorting component tracks sort orders for each attribute and toggles between ascending and descending order.
     * The filtering component supports combined filters from multiple form fields, including checkboxes, radios, selects, and text inputs.
     *
     * @function alpineCode
     * @listens document#alpine:init
     */
    function alpineCode() {
        document.addEventListener("alpine:init", () => {
            /**
             * Alpine.js gridTable component
             * --------------------------------
             * Provides interactive sorting for grid tables using Alpine.js.
             * Tracks sort order for each sortable attribute independently.
             *
             * Properties:
             * - sortOrders: {Object} Tracks current sort order (ascending/descending) for each attribute.
             *
             * Methods:
             * - sortGridTable(attribute, initialSortAsc = true):
             *     Sorts the grid table rows based on the specified data attribute.
             *     The sort order toggles between ascending and descending on each invocation for that attribute.
             *     @param {string} attribute - The data attribute to sort the rows by (e.g., 'wins').
             *     @param {boolean} [initialSortAsc=true] - The initial sort order (ascending if true, descending if false).
             *     Example usage: x-on:click="sortGridTable('wins')"
             */
            Alpine.data("gridTable", () => ({
                // Object to track sort orders for each attribute
                sortOrders: {},
                sortedElement: null,

                sortGridTable(attribute, initialSortAsc = true) {
                    // Retrieve current sort order for the given attribute, defaulting to true (ascending)
                    let sortAsc = this.sortOrders[attribute] ?? initialSortAsc;
                    const rows = Array.from(
                        this.$root.querySelectorAll(".o-gridTable__row:not(.o-gridTable__headerRow)")
                    );
                    rows.sort((a, b) => {
                        const aVal = Number(a.dataset[attribute] ?? 0);
                        const bVal = Number(b.dataset[attribute] ?? 0);
                        return sortAsc ? aVal - bVal : bVal - aVal;
                    });
                    rows.forEach((row) => this.$root.removeChild(row));
                    rows.forEach((row) => this.$root.appendChild(row));
                    // Toggle the sort order for subsequent clicks
                    this.sortOrders[attribute] = !sortAsc;
                    // Update sortedElement for potential UI indication
                    // Remove -sorted class from any previously sorted element
                    const prevSorted = this.$root.querySelector(".-sorted");
                    if (prevSorted) {
                        prevSorted.classList.remove("-sorted");
                    }
                    // Add -sorted class to the currently sorted column
                    this.$el.classList.add("-sorted");
                },
            }));
            /**
             * Alpine.js gridTableFilter component
             * -----------------------------------
             * Provides interactive filtering for grid tables using Alpine.js.
             * Supports combined filters from multiple form fields (checkboxes, radios, selects, text inputs).
             * Each input/select in the form should have a data-filter-attr attribute indicating the row data attribute to filter on.
             *
             * Properties:
             * - None (uses $root and gridElementID for context)
             *
             * Methods:
             * - init():
             *     Initializes the filter logic by calling runCombinedFilter with the form root and grid element ID.
             * - runCombinedFilter(formElement, containerId):
             *     Attaches event listeners to all filter fields and runs filterRows on change or page load.
             *     @param {HTMLElement} formElement - The form element containing filter fields.
             *     @param {string} containerId - The ID of the grid/table container to filter.
             * - filterRows(filters, containerId):
             *     Filters rows within the container based on attribute/value pairs.
             *     Adds the -noStripeCount class to rows that do not match any filter.
             *     Removes the -noStripeCount class from rows that match at least one filter.
             *     @param {Array} filters - Array of filter objects: [{attr: 'team', values: ['NE', 'DET']}]
             *     @param {string} containerId - The ID of the container element to scope the filtering (optional; if not provided, uses document)
             *
             * Example usage:
             *   <form x-data="gridTableFilter('teams')">
             *     ... filter fields ...
             *   </form>
             */
            Alpine.data("gridTableFilter", (gridElementID) => ({
                init() {
                    this.runCombinedFilter(this.$root, gridElementID);
                },

                /**
                 * runCombinedFilter: Attaches event listeners and runs filterRows for any form fields
                 * @param {string} formId - id for the form containing filter fields
                 * @param {string} gridElementID - id for the container to filter
                 *
                 * Each input/select in the form should have a data-filter-attr attribute indicating the row data attribute to filter on.
                 */
                runCombinedFilter(formElement, gridElementID) {
                    const fields = formElement.querySelectorAll("input[data-filter-attr], select[data-filter-attr]");
                    const self = this;
                    function handler() {
                        let filters = [];
                        let processedAttrs = new Set();

                        fields.forEach((field) => {
                            const attr = field.dataset.filterAttr;
                            if (!attr || processedAttrs.has(attr)) return;
                            let values = [];
                            if (field.type === "checkbox") {
                                const checkboxes = formElement.querySelectorAll(
                                    `input[type="checkbox"][data-filter-attr='${attr}'][name='${field.name}']`
                                );
                                if (checkboxes.length > 1) {
                                    // Multiple checkboxes for this name: include all checked, or empty array if none checked
                                    checkboxes.forEach((cb) => {
                                        if (cb.checked) values.push(cb.value);
                                    });
                                    filters.push({ attr: attr, values: values });
                                    processedAttrs.add(attr);
                                    return;
                                } else if (checkboxes.length === 1) {
                                    // Single checkbox: include only if checked
                                    if (checkboxes[0].checked) {
                                        values.push(checkboxes[0].value);
                                        filters.push({ attr: attr, values: values });
                                    }
                                    processedAttrs.add(attr);
                                    return;
                                }
                            } else if (field.tagName === "SELECT") {
                                if (field.value !== "") values.push(field.value);
                            } else if (field.type === "radio") {
                                const radios = formElement.querySelectorAll(
                                    `input[type="radio"][data-filter-attr='${attr}'][name='${field.name}']`
                                );
                                radios.forEach((rb) => {
                                    if (rb.checked && rb.value !== "") values.push(rb.value);
                                });
                            } else {
                                if (field.value !== "") values.push(field.value);
                            }
                            processedAttrs.add(attr);
                            // Only add filter if there are values (for non-checkbox fields)
                            if (values.length > 0) {
                                filters.push({ attr: attr, values: values });
                            }
                        });

                        self.filterRows(filters, gridElementID);
                    }
                    fields.forEach((field) => {
                        field.addEventListener("change", handler);
                    });
                    handler(); // Initial filter on page load
                },

                /**
                 * filterRows: Filters rows within a container based on attribute/value pairs.
                 * Adds the -noStripeCount class to rows that do not match any filter.
                 * Removes the -noStripeCount class from rows that match at least one filter.
                 *
                 * @param {Array} filters - Array of filter objects: [{attr: 'team', values: ['Sharks', 'Lions']}]
                 * @param {string} gridElementID - The id of the container element to scope the filtering (optional; if not provided, uses document)
                 *
                 * Example usage:
                 *   filterRows([
                 *     {attr: 'team', values: ['NE', 'DET']},
                 *     {attr: 'position', values: ['1', '2']}
                 *   ], 'myTableContainer');
                 */
                filterRows(filters, gridElementID) {
                    if (!Array.isArray(filters)) {
                        filters = [filters];
                    }
                    let selector = filters.map((f) => `[data-${f.attr}]`).join(",");
                    let container = gridElementID ? document.getElementById(gridElementID) ?? null : document;
                    if (container) {
                        const filteredRows = container.querySelectorAll(selector);
                        for (let i = 0; i < filteredRows.length; i++) {
                            const domRow = filteredRows[i];
                            let match = true;
                            for (let f = 0; f < filters.length; f++) {
                                let dataValue = domRow.dataset[filters[f].attr];

                                if (!filters[f].values.includes(dataValue)) {
                                    match = false;
                                    break;
                                }
                            }
                            if (match) {
                                domRow.classList.remove("-noStripeCount");
                            } else {
                                domRow.classList.add("-noStripeCount");
                            }
                        }
                    }
                },
            }));
        });
    }

    return {
        hideTakenPlayers: hideTakenPlayers,
        init: init,
    };
})();
export { toggleActions };
